// frontend/src/components/admin/ChatReportsManagement.jsx
import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import api from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import {
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  User,
  MessageCircle,
  Flag,
  ChevronDown,
  ChevronUp,
  FileText,
  Shield,
  UserX,
} from "lucide-react";

export default function ChatReportsManagement() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReport, setExpandedReport] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    reviewed: 0,
    dismissed: 0,
  });

  useEffect(() => {
    loadReports();
    loadStats();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/chat-reports");
      if (response.data.success) {
        setReports(response.data.data);
      }
    } catch (error) {
      console.error("Error loading chat reports:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar as denúncias de chat.",
        confirmButtonColor: "#449879",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get("/admin/chat-reports/stats");
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleReview = async (reportId) => {
    try {
      const response = await api.patch(`/admin/chat-reports/${reportId}/review`);
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Denúncia revisada!",
          timer: 1500,
          showConfirmButton: false,
          customClass: {
            popup: "rounded-2xl",
          },
        });
        loadReports();
        loadStats();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível revisar a denúncia.",
        confirmButtonColor: "#449879",
      });
    }
  };

  const handleDismiss = async (reportId) => {
    const result = await Swal.fire({
      title: "Descartar denúncia?",
      text: "Esta denúncia será marcada como descartada.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6b7280",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Sim, descartar",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "rounded-2xl",
        confirmButton: "rounded-xl px-6 py-2.5",
        cancelButton: "rounded-xl px-6 py-2.5",
      },
    });

    if (result.isConfirmed) {
      try {
        const response = await api.patch(`/admin/chat-reports/${reportId}/dismiss`);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Denúncia descartada!",
            timer: 1500,
            showConfirmButton: false,
            customClass: {
              popup: "rounded-2xl",
            },
          });
          loadReports();
          loadStats();
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Não foi possível descartar a denúncia.",
          confirmButtonColor: "#449879",
        });
      }
    }
  };

  const toggleExpand = (reportId) => {
    setExpandedReport(expandedReport === reportId ? null : reportId);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" />
            Pendente
          </span>
        );
      case "reviewed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Revisada
          </span>
        );
      case "dismissed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <XCircle className="w-3 h-3" />
            Descartada
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#449879] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="bg-yellow-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          <p className="text-sm text-yellow-600">Pendentes</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.reviewed}</p>
          <p className="text-sm text-green-600">Revisadas</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-600">{stats.dismissed}</p>
          <p className="text-sm text-gray-500">Descartadas</p>
        </div>
      </div>

      {/* Lista de denuncias */}
      {reports.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-2xl">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhuma denúncia de chat encontrada</p>
          <p className="text-sm text-gray-400 mt-1">
            Aguardando denúncias de conversas.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <div
              key={report.id}
              className={`bg-white rounded-2xl border overflow-hidden transition-all ${
                report.status === "pending" ? "border-yellow-200 bg-yellow-50/30" : "border-gray-200"
              }`}
            >
              {/* Header de la denuncia */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(report.id)}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-gray-800 truncate flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-[#449879]" />
                        Denúncia de conversa
                      </h4>
                      {getStatusBadge(report.status)}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Reportado por: {report.reporter?.apelido || report.reporter?.name || "Usuário"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Flag className="w-3 h-3 text-red-400" />
                        Motivo: {report.reason}
                      </span>
                      <span className="flex items-center gap-1 text-xs">
                        <Clock className="w-3 h-3" />
                        {new Date(report.created_at).toLocaleDateString("pt-BR")}
                      </span>
                    </div>
                    {/* 🔥 Mensaje denunciado - siempre visible */}
                    {report.message?.text && (
                      <div className="mt-2 p-2 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600 line-clamp-2">
                          💬 "{report.message.text}"
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Enviado por: {report.message.sender?.apelido || report.message.sender?.name || "Desconocido"}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {report.status === "pending" && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReview(report.id);
                          }}
                          className="bg-green-50 hover:bg-green-100 text-green-600 p-2 rounded-xl transition-colors"
                          title="Marcar como revisada"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDismiss(report.id);
                          }}
                          className="bg-gray-50 hover:bg-gray-100 text-gray-600 p-2 rounded-xl transition-colors"
                          title="Descartar"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <div className="shrink-0">
                      {expandedReport === report.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 🔥 DETALLES EXPANDIDOS CON USUARIO DENUNCIADO Y CHAT */}
              {expandedReport === report.id && (
                <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Columna 1: Denunciante */}
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-1 text-blue-500" />
                        Denunciante
                      </h5>
                      <div className="space-y-2 text-sm bg-white p-3 rounded-xl">
                        <p>
                          <strong>Nombre:</strong> {report.reporter?.name || "No disponible"}
                        </p>
                        <p>
                          <strong>Apelido:</strong> {report.reporter?.apelido || "No disponible"}
                        </p>
                        <p>
                          <strong>Email:</strong> {report.reporter?.email || "No disponible"}
                        </p>
                      </div>
                    </div>

                    {/* 🔥 NUEVO: Columna 2 - Usuario Denunciado */}
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">
                        <UserX className="w-4 h-4 inline mr-1 text-red-500" />
                        Usuario Denunciado
                      </h5>
                      <div className="space-y-2 text-sm bg-white p-3 rounded-xl">
                        {report.reported_user ? (
                          <>
                            <p>
                              <strong>Nombre:</strong> {report.reported_user.name || "No disponible"}
                            </p>
                            <p>
                              <strong>Apelido:</strong> {report.reported_user.apelido || "No disponible"}
                            </p>
                            <p>
                              <strong>Email:</strong> {report.reported_user.email || "No disponible"}
                            </p>
                          </>
                        ) : (
                          <p className="text-gray-400">Usuario no disponible</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 🔥 NUEVO: Chat denunciado - ocupa toda la fila */}
                  <div className="mt-4">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-[#449879]" />
                      Chat denunciado
                      {report.conversation_id && (
                        <span className="text-xs text-gray-400 font-normal">
                          (Conversa #{report.conversation_id})
                        </span>
                      )}
                    </h5>
                    
                    {report.chat_messages && report.chat_messages.length > 0 ? (
                      <div className="bg-gray-100 rounded-xl p-4 max-h-[400px] overflow-y-auto space-y-2">
                        {report.chat_messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.is_from_reporter ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-xl px-3 py-2 ${
                                msg.is_from_reporter
                                  ? 'bg-[#449879] text-white rounded-br-sm'
                                  : 'bg-white text-gray-800 rounded-bl-sm shadow-sm'
                              }`}
                            >
                              {/* Nombre del remitente */}
                              <p className="text-[10px] font-medium opacity-70 mb-0.5">
                                {msg.is_from_reporter ? '🗣️ Denunciante' : '👤 Denunciado'}
                                {' - '}
                                {msg.sender_apelido || msg.sender_name || 'Usuário'}
                              </p>
                              {/* Mensaje */}
                              <p className="text-sm leading-relaxed break-words">
                                {msg.message}
                              </p>
                              {/* Fecha */}
                              <p className={`text-[9px] mt-0.5 ${
                                msg.is_from_reporter ? 'text-green-100/70' : 'text-gray-400'
                              }`}>
                                {new Date(msg.created_at).toLocaleString('pt-BR')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 bg-gray-50 p-4 rounded-xl text-center">
                        No hay mensajes en esta conversación
                      </p>
                    )}
                  </div>

                  {/* Detalles de la denuncia */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">
                        <FileText className="w-4 h-4 inline mr-1 text-gray-500" />
                        Detalles
                      </h5>
                      <div className="space-y-2 text-sm bg-white p-3 rounded-xl">
                        <p>
                          <strong>Motivo:</strong> {report.reason}
                        </p>
                        {report.description && (
                          <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                            <p className="text-gray-600">{report.description}</p>
                          </div>
                        )}
                        <p>
                          <strong>Fecha:</strong> {new Date(report.created_at).toLocaleString("pt-BR")}
                        </p>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">
                        <Shield className="w-4 h-4 inline mr-1 text-gray-500" />
                        Estado
                      </h5>
                      <div className="space-y-2 text-sm bg-white p-3 rounded-xl">
                        <p>
                          <strong>Status:</strong> {getStatusBadge(report.status)}
                        </p>
                        {report.admin_notes && (
                          <p className="mt-2 p-2 bg-blue-50 rounded-lg text-blue-700">
                            <strong>Notas do admin:</strong> {report.admin_notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}